<script lang="ts">
    import { page } from '$app/stores';
    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { NextPageNavigator } from '$lib/global/helper/NextPageNavigator';
    import { htmlEmbedFirstInteraction } from '$lib/global/tracking/v4';
    import { cn } from '$lib/utils/cn';

    import {
        receiveIframeDimensionsUpdates,
        sendSetBodyContentMessage,
        sendSetHeadContentMessage,
    } from '@perspective-software/cross-origin-html-embed';
    import unescape from 'lodash/unescape';
    import { onDestroy } from 'svelte';

    import { NORMALIZE_CSS } from '../constants';
    import { createUniqueCrossOriginIframeSandboxUrl } from '../helpers';

    // Please also check out "Notes.md" for more details on "first interaction detection".

    export let head = '';
    export let body = '';
    export let enforceCenteredCustomHtmlContent = '';
    export let id = '';
    export let campaignId = '';
    export let firstResultPage = '';
    export let nextPage = '';

    const {
        data: { pageIdSlugMap },
    } = $page;

    const nextPageNavigator = new NextPageNavigator(campaignId, firstResultPage, pageIdSlugMap);

    const iframeUrl = createUniqueCrossOriginIframeSandboxUrl(`${campaignId}-${id}`);
    let iframeHeight = 0;
    let unsubscribeReceiveDimensionsUpdates: (() => void) | null = null;
    let unsubscribeFirstInteractionListener: (() => void) | null = null;
    let unsubscribePageNavigationActionsListener: (() => void) | null = null;

    onDestroy(() => {
        unsubscribeFirstInteractionListener?.();
        unsubscribePageNavigationActionsListener?.();
        unsubscribeReceiveDimensionsUpdates?.();
    });

    const headHtmlToInject = `
            ${NORMALIZE_CSS}
            <style>
                body {
                    width: 100%;
                    ${
                        enforceCenteredCustomHtmlContent
                            ? 'display: flex; flex-direction: column; justify-content: center; align-items: center;'
                            : ''
                    }
                }
                style, script, title {
                    display: none !important;
                }
                iframe {
                    max-width: 100% !important;
                }
            </style>
            ${unescape(head)}
    `;

    const bodyHtmlToInject = `
        <script>
            (function() {
                function navigateToNextPage() {
                    window.parent.postMessage({
                        _isSandboxMessage: true,
                        type: "navigate-to-next-page"
                    }, "${window.location.origin}");
                }

                function navigateToResultPage() {
                    window.parent.postMessage({
                        _isSandboxMessage: true,
                        type: "navigate-to-result-page"
                    }, "${window.location.origin}");
                }

                function navigateToPageBySlug(pageSlug) {
                    window.parent.postMessage({
                        _isSandboxMessage: true,
                        type: "navigate-to-page-by-slug",
                        pageSlug: pageSlug
                    }, "${window.location.origin}");
                }

                window.perspective = {
                    navigateToNextPage: navigateToNextPage,
                    navigateToResultPage: navigateToResultPage,
                    navigateToPageBySlug: navigateToPageBySlug,
                };
            })();
        ${'<'}/script>

        <script>
            (function() {
                const initiallyActiveElement = document.activeElement;
                let activeElementCheckInterval = undefined;
                let removeClickListener = null;

                function firstInteractionDetected() {
                    window.parent.postMessage({
                        _isSandboxMessage: true,
                        type: "first-interaction-detected"
                    }, "${window.location.origin}");

                    clearTimeout(activeElementCheckInterval);

                    if ( removeClickListener ) {
                        removeClickListener();
                    }
                }

                activeElementCheckInterval = setInterval(() => {
                    const activeElement = document.activeElement;

                    if ( activeElement !== initiallyActiveElement && activeElement !== document.body && activeElement.tagName === "IFRAME" ) {
                        firstInteractionDetected();
                    }
                }, 333);

                const clickListener = (e) => {
                    if ( e.target !== initiallyActiveElement && e.target !== document.body ) {
                        firstInteractionDetected();
                    }
                };

                document.addEventListener("click", clickListener, true);
                removeClickListener = () => {
                    document.removeEventListener("click", clickListener, true);
                }
            })();
        ${'<'}/script>

        ${unescape(body)}
    `;

    // We need to listen for message sent via postMessage
    // to track a first interaction that has been detected
    // the sandbox iframe.
    const listenForFirstInteraction = (iframe: HTMLIFrameElement) => {
        const messageListener = (event: MessageEvent) => {
            if (event.origin === iframeUrl.origin || event.source === iframe.contentWindow) {
                const message = event.data;

                if (
                    message &&
                    message._isSandboxMessage === true &&
                    message.type === 'first-interaction-detected'
                ) {
                    htmlEmbedFirstInteraction({ embedType: 'sandboxed' });
                }
            }
        };

        window.addEventListener('message', messageListener);

        return () => {
            window.removeEventListener('message', messageListener);
        };
    };

    // We need to listen for message from the iframe to perform
    // page navigation actions triggered from the iframe embed code.
    const listenForPageNavigationActions = (iframe: HTMLIFrameElement) => {
        const messageListener = (event: MessageEvent) => {
            if (event.origin === iframeUrl.origin || event.source === iframe.contentWindow) {
                const message = event.data;

                if (!message || message._isSandboxMessage !== true) {
                    return;
                }

                if (message.type === 'navigate-to-next-page') {
                    nextPageNavigator.go(nextPage);
                } else if (message.type === 'navigate-to-result-page') {
                    nextPageNavigator.go('result');
                } else if (
                    message.type === 'navigate-to-page-by-slug' &&
                    typeof message.pageSlug === 'string'
                ) {
                    nextPageNavigator.go(message.pageSlug);
                }
            }
        };

        window.addEventListener('message', messageListener);

        return () => {
            window.removeEventListener('message', messageListener);
        };
    };

    const onIframeLoad = (evt: Event) => {
        const iframe = evt.target as HTMLIFrameElement;

        unsubscribeFirstInteractionListener = listenForFirstInteraction(iframe);
        unsubscribePageNavigationActionsListener = listenForPageNavigationActions(iframe);

        unsubscribeReceiveDimensionsUpdates = receiveIframeDimensionsUpdates(iframe, ({ data }) => {
            iframeHeight = data.documentElementHeight;
        });

        sendSetHeadContentMessage(iframe, headHtmlToInject);
        sendSetBodyContentMessage(iframe, bodyHtmlToInject);
    };

    const embedBorderRadiusClass = getBorderRadius('embed');

    // Taken from here: https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Permissions-Policy
    const allowPermissions = [
        'accelerometer',
        'ambient-light-sensor',
        'autoplay',
        'battery',
        'bluetooth',
        'browsing-topics',
        'camera',
        'display-capture',
        'document-domain',
        'encrypted-media',
        'execution-while-not-rendered',
        'execution-while-out-of-viewport',
        'fullscreen',
        'gamepad',
        'geolocation',
        'gyroscope',
        'hid',
        'identity-credentials-get',
        'idle-detection',
        'local-fonts',
        'magnetometer',
        'microphone',
        'midi',
        'otp-credentials',
        'payment',
        'picture-in-picture',
        'publickey-credentials-create',
        'publickey-credentials-get',
        'screen-wake-lock',
        'serial',
        'speaker-selection',
        'storage-access',
        'usb',
        'web-share',
        'window-management',
        'xr-spatial-tracking',
    ]
        .map((entry) => entry + ' *')
        .join('; ');
</script>

<div class="fun-flex fun-justify-center">
    <iframe
        src={iframeUrl.href}
        class={cn('fun-w-full max-sm:fun-rounded-none', embedBorderRadiusClass)}
        title="Custom HTML Embed"
        loading="eager"
        scrolling="no"
        style={`height: ${iframeHeight}px;`}
        on:load={onIframeLoad}
        allow={allowPermissions}
        allowfullscreen
    />
</div>
